<template>
  <div class="p-6 dark:text-gray-50" v-if="canAdd || user?.is_staff">
    <div class="flex space-x-3 mb-6">
      <button
        @click="goBack"
        class="bg-gray-600 text-white px-4 py-1.5 rounded-md hover:bg-gray-700 text-sm flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
        </svg>
        Back
      </button>
      <h1 class="text-2xl font-semibold dark:text-white">{{ isEditMode ? 'Edit' : 'Add' }} API Exporter Job</h1>
    </div>

    <!-- Page-level Error Banner -->
    <div v-if="pageError" class="bg-red-50 dark:bg-red-900/20 border border-red-400 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4 flex items-center">
      <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span class="font-medium">{{ pageError }}</span>
    </div>

    <form @submit.prevent="submitForm" class="space-y-4 bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <JobDescriptionInput v-model="formData.job_description" :error="fieldErrors.job_description" :job-name="jobName" />

      <!-- Target URL -->
      <div class="flex flex-col">
        <label for="target" class="mb-1 font-medium">
          Target URL<span class="text-red-500">*</span>
        </label>
        <input
          id="target"
          v-model="formData.target"
          type="url"
          required
          placeholder="https://10.32.1.6:443"
          :class="inputClass('target')"
        />
        <p class="text-sm text-gray-500 mt-1">Fortigate device URL including port (e.g., https://IP:443)</p>
        <FieldError :message="fieldErrors.target" />
      </div>

      <!-- API Token -->
      <div class="flex flex-col">
        <label for="token" class="mb-1 font-medium">
          API Token<span class="text-red-500">*</span>
        </label>
        <input
          id="token"
          v-model="formData.token"
          type="password"
          required
          placeholder="Fortigate API token"
          :class="inputClass('token')"
        />
        <FieldError :message="fieldErrors.token" />
      </div>

      <!-- Profile -->
      <div class="flex flex-col">
        <label for="fortigate_profile" class="mb-1 font-medium">
          Profile <span class="text-gray-500 text-sm">(optional)</span>
        </label>
        <div class="flex items-center space-x-2">
          <GlassSelect
            v-model="formData.fortigate_profile"
            :options="profileOptions"
            :searchable="true"
            valueKey="value"
            labelKey="label"
            placeholder="-- No profile (use defaults) --"
            :error="!!fieldErrors.fortigate_profile"
            class="flex-1"
          />
          <router-link
            :to="{ name: 'AddFortigateProfile' }"
            class="text-blue-600 hover:text-blue-800 text-sm whitespace-nowrap"
          >
            + Create new
          </router-link>
        </div>
        <p class="text-sm text-gray-500 mt-1">
          Select a profile to define which URL paths to include/exclude.
          <span v-if="selectedProfileInfo">
            ({{ selectedProfileInfo.include_probes_count || 0 }} include, {{ selectedProfileInfo.exclude_probes_count || 0 }} exclude)
          </span>
        </p>
        <FieldError :message="fieldErrors.fortigate_profile" />
      </div>

      <!-- Metrics Exclude (Optional) -->
      <div class="flex flex-col">
        <label for="metrics_exclude" class="mb-1 font-medium">
          Metrics Exclude <span class="text-gray-500 text-sm">(optional)</span>
        </label>
        <textarea
          id="metrics_exclude"
          v-model="formData.metrics_exclude"
          rows="3"
          placeholder="Regex pattern to exclude metrics (e.g., fortigate_interface.*)"
          class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        ></textarea>
      </div>

      <!-- Scrape Settings -->
      <div class="grid grid-cols-2 gap-4">
        <div class="flex flex-col">
          <label for="scrape_interval" class="mb-1 font-medium">Scrape Interval (seconds)</label>
          <input
            id="scrape_interval"
            v-model.number="formData.scrape_interval"
            type="number"
            min="10"
            class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div class="flex flex-col">
          <label for="scrape_timeout" class="mb-1 font-medium">Scrape Timeout (seconds)</label>
          <input
            id="scrape_timeout"
            v-model.number="formData.scrape_timeout"
            type="number"
            min="5"
            class="border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end space-x-2 pt-4">
        <button
          type="button"
          @click="goBack"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {{ isEditMode ? 'Update' : 'Create' }} API Job
        </button>
      </div>
    </form>
  </div>

  <PageError v-else-if="permsLoaded" :error="'Permission denied'" :errorCode="'403'" />
</template>

<script>
import { createAlloyFortigate, getAlloyFortigateById, updateAlloyFortigate } from "@/services/alloyService";
import { getAllFortigateProfiles } from "@/services/fortigateProfileService";
import FieldError from "@/components/FieldError.vue";
import JobDescriptionInput from "@/components/JobDescriptionInput.vue";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "AddAlloyFortigate",
  components: { FieldError, JobDescriptionInput, PageError, GlassSelect },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      fieldErrors: {},
      pageError: null,
      jobName: "",
      formData: {
        job_description: "",
        target: "",
        token: "",
        fortigate_profile: null,
        metrics_exclude: "",
        scrape_interval: 60,
        scrape_timeout: 30,
      },
      availableProfiles: [],
      canAdd: false,
      isLoading: false,
    };
  },
  computed: {
    currentSchema() { return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null; },
    profileOptions() {
      return this.availableProfiles.map((p) => ({
        value: p.id,
        label: p.description ? `${p.name} - ${p.description}` : p.name,
      }));
    },
    user() {
      return useStore.getUser();
    },
    permsLoaded() {
      return useStore.permissionsLoadedRef.value;
    },
    configId() {
      return this.$route.params.configId;
    },
    jobId() {
      return this.$route.params.jobId;
    },
    isEditMode() {
      return !!this.jobId;
    },
    selectedProfileInfo() {
      if (!this.formData.fortigate_profile) return null;
      return this.availableProfiles.find(p => p.id === this.formData.fortigate_profile);
    },
    autoGeneratedJobName() {
      if (this.isEditMode) {
        return "Auto-generated based on entity";
      }
      return "Will be generated after creation";
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.checkPermissions();
    await this.loadAvailableProfiles();
    if (this.isEditMode) {
      await this.loadExistingJob();
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canAdd = await useStore.hasPermission("Can add Alloy Fortigate Exporter Job");
      }
    },
    async loadAvailableProfiles() {
      try {
        this.availableProfiles = await getAllFortigateProfiles(this.currentSchema);
      } catch (error) {
        console.error("Error loading Fortigate profiles:", error);
      }
    },
    async loadExistingJob() {
      this.isLoading = true;
      try {
        const job = await getAlloyFortigateById(this.jobId, this.currentSchema);
        this.jobName = job.job_name;
        this.formData.job_description = job.job_description || "";
        this.formData.target = job.target;
        this.formData.token = job.token || "";
        // Handle both new FK and legacy profile string
        this.formData.fortigate_profile = job.fortigate_profile || null;
        this.formData.metrics_exclude = job.metrics_exclude || "";
        this.formData.scrape_interval = job.scrape_interval || 60;
        this.formData.scrape_timeout = job.scrape_timeout || 30;
      } catch (error) {
        console.error("Error loading Fortigate job:", error);
        alert("Failed to load job data");
        this.goBack();
      } finally {
        this.isLoading = false;
      }
    },
    inputClass(field) {
      return [
        'border rounded px-3 py-2 focus:outline-none focus:ring-2 dark:bg-gray-600',
        this.fieldErrors[field] ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-300 focus:ring-blue-500'
      ];
    },
    goBack() {
      this.$router.push({ name: "AlloyDetails", params: { id: this.configId }, query: { schemaName: this.currentSchema, tab: "fortigate" } });
    },
    async submitForm() {
      this.pageError = null;
      this.fieldErrors = {};

      const data = {
        alloy_config: parseInt(this.configId),
        job_description: this.formData.job_description.trim(),
        target: this.formData.target,
        token: this.formData.token,
        fortigate_profile: this.formData.fortigate_profile,
        scrape_interval: this.formData.scrape_interval,
        scrape_timeout: this.formData.scrape_timeout,
      };

      if (this.formData.metrics_exclude) data.metrics_exclude = this.formData.metrics_exclude;

      try {
        if (this.isEditMode) {
          await updateAlloyFortigate(this.jobId, data, this.currentSchema);
        } else {
          await createAlloyFortigate(data, this.currentSchema);
        }
        this.goBack();
      } catch (error) {
        this.pageError = null;
        this.fieldErrors = {};

        const errData = error?.response?.data;
        if (errData) {
          // Handle field-specific errors
          Object.keys(errData).forEach((key) => {
            const errorMsg = Array.isArray(errData[key]) ? errData[key][0] : errData[key];
            this.fieldErrors[key] = errorMsg;
          });

          // Show page-level error for non-field errors or alloy_config errors
          // Replace technical backend error messages with user-friendly versions
          if (errData.alloy_config || errData.non_field_errors) {
            let errorMessage = errData.alloy_config?.[0] || errData.alloy_config ||
                               errData.non_field_errors?.[0] || errData.non_field_errors;

            // Replace technical error with client-friendly message
            if (typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('mimir')) {
              errorMessage = "Your organization tenant is not configured. Please contact Splitpoint support for assistance.";
            }

            this.pageError = errorMessage || "Please correct the errors below";
          } else if (Object.keys(errData).length > 0) {
            this.pageError = "Please correct the errors below";
          }
        } else {
          // Generic error message for network/server errors
          this.pageError = `Failed to ${this.isEditMode ? 'update' : 'create'} job. Please try again.`;
        }

        console.error(`Error ${this.isEditMode ? 'updating' : 'creating'} Fortigate job:`, error);
      }
    },
  },
};
</script>
